import { useState, useEffect } from 'react';

/**
 * Custom hook to monitor network status and handle image reloading
 * when network reconnects after being offline
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  useEffect(() => {
    const handleOnline = () => {
      console.log('Network: Online');
      setIsOnline(true);
      
      // If we were offline before, reload all images
      if (wasOffline) {
        console.log('Network reconnected - reloading images...');
        
        // Increment reload trigger to force React components to re-render
        setReloadTrigger(prev => prev + 1);
        
        // First attempt: reload images immediately
        reloadAllImages();
        
        // Clear service worker image cache if available
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_IMAGE_CACHE'
          });
        }
        
        // Start mutation observer to catch dynamically loaded images
        startImageObserver();
        
        // Second attempt: reload images again after 1 second (for dynamically loaded content)
        setTimeout(() => {
          console.log('Second image reload attempt...');
          reloadAllImages();
        }, 1000);
        
        // Third attempt: reload images after 3 seconds (for lazy-loaded content)
        setTimeout(() => {
          console.log('Third image reload attempt...');
          reloadAllImages();
        }, 3000);
        
        // Fourth attempt: reload images after 5 seconds (final sweep)
        setTimeout(() => {
          console.log('Final image reload attempt...');
          reloadAllImages();
          stopImageObserver();
        }, 5000);
        
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      console.log('Network: Offline');
      setIsOnline(false);
      setWasOffline(true);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      stopImageObserver();
    };
  }, [wasOffline]);

  return { isOnline, wasOffline, reloadTrigger };
};

// MutationObserver to watch for dynamically added images
let imageObserver = null;

const startImageObserver = () => {
  if (imageObserver) return; // Already observing
  
  const timestamp = new Date().getTime();
  
  imageObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        // Check if the added node is an image
        if (node.nodeName === 'IMG') {
          reloadSingleImage(node, timestamp);
        }
        // Check if the added node contains images
        if (node.querySelectorAll) {
          const images = node.querySelectorAll('img');
          images.forEach(img => reloadSingleImage(img, timestamp));
        }
      });
    });
  });
  
  imageObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  console.log('Image observer started');
};

const stopImageObserver = () => {
  if (imageObserver) {
    imageObserver.disconnect();
    imageObserver = null;
    console.log('Image observer stopped');
  }
};

const reloadSingleImage = (img, timestamp) => {
  if (img.src && !img.src.includes('data:') && !img.src.includes('blob:')) {
    const originalSrc = img.src.split('?')[0].split('#')[0];
    img.src = `${originalSrc}?t=${timestamp}`;
  }
};

/**
 * Reload all images on the page by adding a cache-busting timestamp
 * Includes special handling for Leaflet map markers and React components
 */
const reloadAllImages = () => {
  const timestamp = new Date().getTime();
  
  // Reload regular img tags
  const images = document.querySelectorAll('img');
  images.forEach((img) => {
    if (img.src && !img.src.includes('data:') && !img.src.includes('blob:')) {
      const originalSrc = img.src.split('?')[0].split('#')[0]; // Remove query params and hash
      img.src = `${originalSrc}?t=${timestamp}`;
    }
  });
  
  // Reload background images
  const elementsWithBgImage = document.querySelectorAll('[style*="background-image"]');
  elementsWithBgImage.forEach((element) => {
    const style = element.style.backgroundImage;
    if (style && style.includes('url(')) {
      const urlMatch = style.match(/url\(['"]?([^'"]+)['"]?\)/);
      if (urlMatch && urlMatch[1] && !urlMatch[1].includes('data:') && !urlMatch[1].includes('blob:')) {
        const originalUrl = urlMatch[1].split('?')[0].split('#')[0];
        element.style.backgroundImage = `url("${originalUrl}?t=${timestamp}")`;
      }
    }
  });
  
  // Special handling for Leaflet map markers
  const leafletMarkers = document.querySelectorAll('.leaflet-marker-icon');
  leafletMarkers.forEach((marker) => {
    if (marker.src && !marker.src.includes('data:') && !marker.src.includes('blob:')) {
      const originalSrc = marker.src.split('?')[0].split('#')[0];
      marker.src = `${originalSrc}?t=${timestamp}`;
    }
  });
  
  // Force reload of all CSS background images in leaflet tiles
  const leafletTiles = document.querySelectorAll('.leaflet-tile');
  leafletTiles.forEach((tile) => {
    if (tile.src && !tile.src.includes('data:')) {
      const originalSrc = tile.src.split('?')[0].split('#')[0];
      tile.src = `${originalSrc}?t=${timestamp}`;
    }
  });
  
  // Trigger a page-wide image reload event for custom components
  window.dispatchEvent(new CustomEvent('imagesReloaded', { detail: { timestamp } }));
  
  console.log(`Reloaded ${images.length} images, ${elementsWithBgImage.length} background images, ${leafletMarkers.length} map markers, and ${leafletTiles.length} map tiles`);
};

/**
 * Hook to add error handling and retry logic for individual images
 */
export const useImageErrorHandler = () => {
  const handleImageError = (event, retryCount = 0, maxRetries = 3) => {
    const img = event.target;
    
    if (retryCount < maxRetries && navigator.onLine) {
      console.log(`Image load failed, retrying (${retryCount + 1}/${maxRetries})...`);
      
      // Wait a bit before retrying
      setTimeout(() => {
        const originalSrc = img.src.split('?')[0];
        const timestamp = new Date().getTime();
        img.src = `${originalSrc}?retry=${retryCount + 1}&t=${timestamp}`;
      }, 1000 * (retryCount + 1)); // Exponential backoff
    } else {
      console.error('Image failed to load after retries:', img.src);
    }
  };

  return { handleImageError };
};

/**
 * Hook to force image reload with cache busting
 * Use this in components that display images
 */
export const useImageReload = (imageSrc) => {
  const [reloadedSrc, setReloadedSrc] = useState(imageSrc);
  
  useEffect(() => {
    const handleImageReload = (event) => {
      if (imageSrc && !imageSrc.includes('data:') && !imageSrc.includes('blob:')) {
        const originalSrc = imageSrc.split('?')[0].split('#')[0];
        const timestamp = event.detail.timestamp || new Date().getTime();
        setReloadedSrc(`${originalSrc}?t=${timestamp}`);
      }
    };
    
    window.addEventListener('imagesReloaded', handleImageReload);
    
    return () => {
      window.removeEventListener('imagesReloaded', handleImageReload);
    };
  }, [imageSrc]);
  
  return reloadedSrc;
};
